import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';

const TP = 'pages.sneakers.upperMaterial.upperMaterialValue';

interface IUpperMaterialNutritionFactPage extends IProductLandingPage {
  metaDescription?: string;
}

const UpperMaterialNutritionFactPage = ({
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  upperMaterialValue,
  isCrawler = false,
  consumerSearchInitialData = '{}',
}: IUpperMaterialNutritionFactPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = '',
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: t(`${TP}.sneakers`, 'Sneakers'), slug: `/sneakers` },
        {
          name: startCase(upperMaterialValue),
          slug: `/`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { upperMaterialValue }: { upperMaterialValue: string } = query;
  const q = QueryString.parse(query);
  const displayName: string = startCase(upperMaterialValue);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.SNEAKERS}/upper-material/${upperMaterialValue}`,
  });
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnable =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  const consumerSearchFilters = {
    upperMaterials: upperMaterialValue,
    categories: 'footwear',
  };

  if (isConsumerSearchEnable) {
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: {
        ...q,
        pageFilters: {
          upperMaterials: upperMaterialValue,
          categories: 'footwear',
        },
      },
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'sneakers',
        consumerSearchFilters,
        displayName,
        globalPricingId,
        upperMaterialValue,
        isCrawler,
        consumerSearchInitialData: JSON.stringify({}),
      },
    };
  }

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      resolvedUrl,
      query,
      filterKey: 'group_id',
      filterValue: 'sneakers',
    });

    const invalidBrowse = browseResponse.response.total_num_results === 0;

    if (invalidBrowse) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'sneakers',
        constructorResponse: browseResponse,
        consumerSearchFilters,
        displayName,
        globalPricingId,
        upperMaterialValue,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default UpperMaterialNutritionFactPage;
